import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  BookOpen, 
  Users, 
  TrendingUp, 
  Search, 
  Filter,
  Plus,
  Settings,
  BarChart3,
  UserPlus,
  Clock,
  Upload,
  Database
} from 'lucide-react';
import { ALL_COURSE_CATEGORIES } from '@/config/courseNavigation';
import { useSupabaseAuth } from '@/hooks/auth';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useUnifiedCourseCounts } from '@/hooks/useUnifiedCourseCounts';
import { useDatabaseCourseNavigation } from '@/hooks/useDatabaseCourseNavigation';
import CourseMigrationPanel from './CourseMigrationPanel';
import { CourseCountBadge } from '@/components/common/CourseCountBadge';

interface CourseStats {
  totalCourses: number;
  totalAssignments: number;
  activeStudents: number;
  completionRate: number;
}

interface CourseAssignment {
  id: string;
  user_id: string;
  course_id: string;
  created_at: string;
  user_profiles?: {
    display_name?: string;
    email?: string;
  };
}

const AdminCourseManagement: React.FC = () => {
  const { hasAccess } = useSupabaseAuth();
  const { toast } = useToast();
  const { totalCourses: unifiedTotalCourses, totalCategories } = useUnifiedCourseCounts();
  const { categories: databaseCategories } = useDatabaseCourseNavigation();
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<CourseStats>({
    totalCourses: 0,
    totalAssignments: 0,
    activeStudents: 0,
    completionRate: 0
  });
  const [assignments, setAssignments] = useState<CourseAssignment[]>([]);

  useEffect(() => {
    if (hasAccess('admin')) {
      fetchCourseStats();
      fetchCourseAssignments();
    }
  }, [hasAccess]);

  const fetchCourseStats = async () => {
    try {
      setLoading(true);
      
      // Get total assignments
      const { data: assignmentsData, count: totalAssignments } = await supabase
        .from('course_assignments')
        .select('*', { count: 'exact' });

      // Get unique active students
      const { data: activeStudentsData } = await supabase
        .from('course_assignments')
        .select('user_id');

      const uniqueStudents = new Set(activeStudentsData?.map(a => a.user_id)).size;

      // Calculate completion rate
      const { data: completedData } = await supabase
        .from('course_assignments')
        .select('id')
        .eq('status', 'completed');

      const completionRate = totalAssignments && completedData 
        ? Math.round((completedData.length / totalAssignments) * 100)
        : 0;

      setStats({
        totalCourses: unifiedTotalCourses,
        totalAssignments: totalAssignments || 0,
        activeStudents: uniqueStudents,
        completionRate
      });

    } catch (error) {
      console.error('Error fetching course stats:', error);
      toast({
        title: "Error",
        description: "Failed to fetch course statistics",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCourseAssignments = async () => {
    try {
      const { data, error } = await supabase
        .from('course_assignments')
        .select(`
          id,
          user_id,
          course_id,
          created_at,
          user_profiles!inner (
            display_name,
            email
          )
        `)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      setAssignments(data as CourseAssignment[] || []);
    } catch (error) {
      console.error('Error fetching assignments:', error);
    }
  };

  const filteredAssignments = assignments.filter(assignment =>
    assignment.course_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    assignment.user_profiles?.display_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    assignment.user_profiles?.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const statsCards = [
    {
      title: 'Total Courses',
      value: stats.totalCourses,
      icon: BookOpen,
      color: 'text-blue-600'
    },
    {
      title: 'Course Assignments',
      value: stats.totalAssignments,
      icon: UserPlus,
      color: 'text-green-600'
    },
    {
      title: 'Active Students',
      value: stats.activeStudents,
      icon: Users,
      color: 'text-purple-600'
    },
    {
      title: 'Completion Rate',
      value: `${stats.completionRate}%`,
      icon: TrendingUp,
      color: 'text-orange-600'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Course Management</h2>
          <p className="text-muted-foreground">
            Manage courses, assignments, and track learning progress
          </p>
          <CourseCountBadge showIcons={true} className="mt-2" />
        </div>
        <Button className="gap-2">
          <Plus className="h-4 w-4" />
          Bulk Assign Courses
        </Button>
      </div>

      {/* Stats Overview */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {statsCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.title}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{stat.title}</CardTitle>
                <Icon className={`h-4 w-4 ${stat.color}`} />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stat.value}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="assignments" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Course Assignments
          </TabsTrigger>
          <TabsTrigger value="categories" className="flex items-center gap-2">
            <BookOpen className="h-4 w-4" />
            Course Categories
          </TabsTrigger>
          <TabsTrigger value="migration" className="flex items-center gap-2">
            <Upload className="h-4 w-4" />
            Course Migration
          </TabsTrigger>
          <TabsTrigger value="analytics" className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            Analytics
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <Alert>
            <Settings className="h-4 w-4" />
            <AlertDescription>
              <strong>Course Management Dashboard:</strong> Monitor course assignments, 
              track student progress, and manage learning pathways across all technology tracks.
            </AlertDescription>
          </Alert>

          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="h-5 w-5" />
                  Course Categories Overview
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {databaseCategories.slice(0, 6).map((category) => (
                    <div key={category.id} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <category.icon className="h-4 w-4 text-primary" />
                      <span className="text-sm font-medium">{category.title}</span>
                    </div>
                      <Badge variant="secondary">
                        {category.courses.length} courses
                      </Badge>
                    </div>
                  ))}
                  {databaseCategories.length > 6 && (
                    <div className="text-xs text-muted-foreground text-center pt-2">
                      And {databaseCategories.length - 6} more categories...
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Recent Activity
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {assignments.slice(0, 5).map((assignment) => (
                    <div key={assignment.id} className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium">
                          {assignment.user_profiles?.display_name || 'Unknown User'}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Assigned to {assignment.course_id}
                        </div>
                      </div>
                      <Badge variant="secondary">
                        Active
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="assignments" className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search assignments..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <Button variant="outline" className="gap-2">
              <Filter className="h-4 w-4" />
              Filter
            </Button>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Course Assignments</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {filteredAssignments.map((assignment) => (
                  <div 
                    key={assignment.id} 
                    className="flex items-center justify-between p-3 border rounded-lg"
                  >
                    <div className="flex-1">
                      <div className="font-medium">
                        {assignment.user_profiles?.display_name || 'Unknown User'}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {assignment.user_profiles?.email}
                      </div>
                       <div className="text-xs text-muted-foreground mt-1">
                         Course: {assignment.course_id}
                       </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary">
                        Active
                      </Badge>
                      <Button variant="outline" size="sm">
                        Manage
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="categories" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>All Course Categories</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {databaseCategories.map((category) => (
                  <Card key={category.id} className="p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <category.icon className="h-8 w-8 text-primary" />
                      <div>
                        <h3 className="font-semibold">{category.title}</h3>
                        <p className="text-sm text-muted-foreground">
                          {category.courses.length} courses
                        </p>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mb-3">
                      {category.description}
                    </p>
                    <Button variant="outline" size="sm" className="w-full">
                      Manage Category
                    </Button>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="migration" className="space-y-4">
          <CourseMigrationPanel />
        </TabsContent>

        <TabsContent value="analytics" className="space-y-4">
          <Alert>
            <BarChart3 className="h-4 w-4" />
            <AlertDescription>
              Detailed analytics dashboard coming soon. This will include completion rates,
              learning paths analysis, and performance metrics.
            </AlertDescription>
          </Alert>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminCourseManagement;